import { useState } from "react";
import Skeleton from "react-loading-skeleton";
import { CTASection } from "../../../../components/cards/CTASection";
import { Icon } from "../../../../components/icon/icon";
import { SendEmailBlastModal } from "../../../../components/modal/app_modals/sendEmailBlastModal";
import Pagination from "../../../../components/pagination";
import { TableComponent } from "../../../../components/table";
import { MiniHeader } from "../../../../components/typography/miniHeader";
import { BgWrapper } from "../../../../components/wrapper/bgWrapper";
import { useModalControl } from "../../../../customeHooks/useModalControl";
import {
  useGetEmailBlastsQuery,
  useGetEventTicketsBreakdownQuery,
} from "../../../../redux/features/event/event.api";
import { CreatedEvent } from "../../../../redux/features/event/event.types";
import {
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../../../utils/constants";
import {
  formatDate,
  formatTimeWithAmPm,
  parseDateStringToDate,
  subtractDays,
  subtractHoursFromDateTime,
} from "../../../../utils/helpers";
import { EventPermissions, QueryParams } from "../../../../utils/types";
import { EventConfirmationEmailSettings } from "./eventConfirmationEmailSettings";

export const EventEmails = ({
  event,
  totalGuests,
  userEventPermissions,
  refetchEvent,
}: {
  event: CreatedEvent;
  totalGuests: number;
  userEventPermissions: EventPermissions[];
  refetchEvent: () => void;
}) => {
  const [emailBlastsQuery, setEailBlastsQuery] = useState<QueryParams>({
    page: 1,
    limit: 5,
  });

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const {
    data: ticketPurchaseBreakdown,
    isLoading: fetcingTicketsPurchaseBreakdown,
  } = useGetEventTicketsBreakdownQuery(
    {
      id: event._id,
    },
    { refetchOnMountOrArgChange: true },
  );

  const {
    data: emailBlasts,
    isFetching: fetchingEmailBlasts,
    refetch: refetchEmailBlasts,
  } = useGetEmailBlastsQuery(
    {
      eventId: event._id,
      ...emailBlastsQuery,
    },
    { refetchOnMountOrArgChange: true },
  );

  return (
    <>
      <BgWrapper className="flex flex-col gap-3 !rounded-t-none">
        <MiniHeader header="Email Blast" />

        {!fetcingTicketsPurchaseBreakdown && (
          <CTASection
            icon="mail"
            title="Send Email Blast"
            text="We’d send emails to guests that registered for your event"
            titleClassName=""
            iconClassName=""
            extra={undefined}
            onClick={() => {
              handleModalOpen("sendEmailBlastModal");
            }}
            disabled={
              !userEventPermissions.includes("sendEmailBlasts") ||
              totalGuests <= 0
            }
            className="rounded-2xl border border-grey-200 p-3"
          />
        )}

        <div className="flex flex-col gap-3">
          {fetcingTicketsPurchaseBreakdown || fetchingEmailBlasts ? (
            [1, 2].map((i) => {
              return (
                <Skeleton
                  width={"100%"}
                  height={118}
                  baseColor={SKELETON_BASE_COLOR}
                  highlightColor={SKELETON_HIGHLIGHT_COLOR}
                  className="rounded-2xl"
                  key={`${i}`}
                />
              );
            })
          ) : (
            <>
              {emailBlasts?.data?.data?.map((emailBlast, idx) => {
                return (
                  <CTASection
                    icon="check-check"
                    title={emailBlast.subject}
                    text={
                      <div className="flex flex-col gap-[10px]">
                        <p className="line-clamp-1">{emailBlast.body}</p>

                        <div className="flex flex-wrap items-center gap-[10px]">
                          {emailBlast.recipientType === "all" ? (
                            <p className="rounded-full border border-grey-200 px-3 py-2 text-sm font-bold text-primary">
                              All guests
                            </p>
                          ) : (
                            emailBlast?.ticketNames?.map((ticket, idx) => {
                              return (
                                <p
                                  key={`${ticket}-${idx}`}
                                  className="rounded-full border border-grey-200 px-3 py-2 text-sm font-bold text-primary"
                                >
                                  {ticket}
                                </p>
                              );
                            })
                          )}
                        </div>

                        <p className="text-sm font-normal text-grey-900 md:hidden">
                          {formatDate(
                            emailBlast?.sentAt ?? "",
                            "DD MMM, YYYY • h:mm A",
                          )}
                        </p>
                      </div>
                    }
                    titleClassName=""
                    iconClassName=""
                    extra={
                      <p className="hidden text-sm font-normal text-grey-900 md:block">
                        {formatDate(
                          emailBlast?.sentAt ?? "",
                          "DD MMM, YYYY • h:mm A",
                        )}
                      </p>
                    }
                    extraClassName="!self-start"
                    onClick={undefined}
                    disabled={
                      !userEventPermissions.includes("sendEmailBlasts") ||
                      totalGuests <= 0
                    }
                    className="rounded-2xl border border-grey-200 p-3"
                    key={`emailBlast-${idx}`}
                    showCaret={false}
                  />
                );
              })}

              <Pagination
                currentPage={emailBlastsQuery.page ?? 1}
                totalCount={emailBlasts?.data?.total ?? 0}
                itemsPerPage={emailBlastsQuery?.limit ?? 0}
                pageRangeDisplayed={3}
                onPageChange={(page) => {
                  setEailBlastsQuery({
                    ...emailBlastsQuery,
                    page: page,
                  });
                }}
                showPagNumbers={false}
              />
            </>
          )}
        </div>
      </BgWrapper>

      <BgWrapper className="flex flex-col gap-3">
        <EventConfirmationEmailSettings
          event={event}
          refetchEvent={refetchEvent}
          userEventPermissions={userEventPermissions}
        />
      </BgWrapper>

      <BgWrapper className="flex flex-col gap-3">
        <MiniHeader header="Automatic Event Reminders" />

        <TableComponent
          headers={[]}
          rows={[
            {
              id: 1,
              items: [
                <div className="flex items-start gap-4">
                  <Icon
                    className="min-w-fit"
                    svg="alarm-clock"
                    width={20}
                    height={20}
                  />

                  <div className="flex-1 text-grey-900">
                    <p className="text-base font-bold">
                      1 Day Before Event Reminder
                    </p>
                    <p className="text-sm">
                      10:00 AM -{" "}
                      {formatDate(
                        subtractDays(
                          parseDateStringToDate(
                            formatDate(
                              event.startDate,
                              "ddd, MMMM DD, YYYY",
                              true,
                            ),
                            "ddd, MMMM DD, YYYY",
                            true,
                          )!,
                          1,
                        ),
                        "ddd, MMMM DD, YYYY",
                      )}
                    </p>
                  </div>
                </div>,
              ],
            },
            {
              id: 2,
              items: [
                <div className="flex items-start gap-4">
                  <Icon
                    className="min-w-fit"
                    svg="alarm-clock"
                    width={20}
                    height={20}
                  />

                  <div className="flex-1 text-grey-900">
                    <p className="text-base font-bold">Event Day Reminder</p>
                    <p className="text-sm">
                      {formatTimeWithAmPm(
                        subtractHoursFromDateTime(
                          formatDate(
                            event.startDate,
                            "ddd, MMMM DD, YYYY",
                            true,
                          ),
                          event.startTime,
                          1,
                        ),
                      )}{" "}
                      -{" "}
                      {formatDate(
                        subtractHoursFromDateTime(
                          formatDate(
                            event.startDate,
                            "ddd, MMMM DD, YYYY",
                            true,
                          ),
                          event.startTime,
                          1,
                        ),
                        "ddd, MMMM DD, YYYY",
                        true,
                      )}
                    </p>
                  </div>
                </div>,
              ],
            },
          ]}
        />
      </BgWrapper>

      <SendEmailBlastModal
        open={modal === "sendEmailBlastModal" && modalOpen}
        handleClose={() => {
          handleModalClose();
        }}
        event={event}
        onSuccess={() => {
          refetchEmailBlasts();
        }}
        totalGuests={totalGuests}
        ticketPurchaseBreakdown={ticketPurchaseBreakdown}
      />
    </>
  );
};
